import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import Button from "../Button";
import { BellIcon, BagIcon, CardIcon } from "../Icons";
import { useAppContext } from "../../context/AppContext";

/**
 * Asks before calling a waiter, since the round button alone does not say what it does,
 * and reminds the guest that ordering and paying need no waiter.
 */
const CallWaiterSheet = ({ open, onConfirm, onClose, onOrder, onPay, calling }) => {
  const { t } = useTranslation();
  const { tableLabel } = useAppContext();

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const shortcut =
    "flex-1 h-11 rounded-md bg-surface border-[1.5px] border-secondary-dark text-sm font-bold text-dark flex items-center justify-center gap-2";

  return (
    <div className="fixed inset-0 z-[1100] flex items-end animate-fade-in">
      <button
        type="button"
        aria-label={t("Close")}
        className="absolute inset-0 bg-dark/40"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="call-waiter-title"
        className="relative w-full bg-background rounded-t-xl shadow-sheet px-6 pt-6 flex flex-col gap-5 animate-slide-up"
        style={{ paddingBottom: "calc(24px + env(safe-area-inset-bottom, 0px))" }}
      >
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-14 h-14 rounded-full bg-primary-50 text-primary flex items-center justify-center">
            <BellIcon size={28} />
          </div>
          <h2 id="call-waiter-title" className="text-lg font-bold text-dark">
            {t("callWaiterTitle", { table: tableLabel })}
          </h2>
          <p className="text-sm text-muted">{t("callWaiterDescription")}</p>
        </div>

        <div className="rounded-lg bg-secondary px-4 py-3 flex flex-col gap-3">
          <p className="text-sm text-dark">{t("callWaiterSelfServe")}</p>
          <div className="flex gap-2">
            <button type="button" className={shortcut} onClick={onOrder}>
              <BagIcon size={18} />
              {t("Order")}
            </button>
            <button type="button" className={shortcut} onClick={onPay}>
              <CardIcon size={18} />
              {t("Pay")}
            </button>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Button size="lg" fullWidth autoFocus disabled={calling} onClick={onConfirm}>
            {t("callWaiterConfirm")}
          </Button>
          <Button variant="secondary" fullWidth onClick={onClose}>
            {t("Cancel")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CallWaiterSheet;
